/**
 * Pre-order and single-serve variants, per buy box.
 *
 * `blocks/buy-buttons.liquid` prints a map of the variants that need
 * something other than Stay.ai into `data-ora-variants` on each
 * product-form-component:
 *
 * - A **pre-order** variant carries its own selling plan, ship date and price
 *   row in place of Stay.ai.
 * - A **single serve** is one-time only (see `snippets/ora-single-serve.liquid`):
 *   it shows the price row at the variant's own price, hides Stay.ai, and the
 *   form sends no selling plan at all. Every selling_plan field in the form is
 *   disabled, so FormData skips it, and given back when the shopper moves to a
 *   size that can subscribe -- disabled rather than removed, because Stay.ai's
 *   own input has to be there again when they do. `data-ora-oneoff` on the form
 *   tells the other scripts that add a plan (the quick-add bridge in
 *   `ora-quick-add.js`, `ora-purchase-options.js`) to stand down.
 *
 * This used to be the block's own `{% javascript %}`, which Shopify bundles
 * only onto pages that render the block -- so on a collection page, where the
 * only buy box is the one the quick-add popup brings in, it never ran, and the
 * magnesium single serve went into the cart on Stay.ai's default subscription.
 * The map moved from a script tag to an attribute for the same popup: the
 * quick add carries the buy box across and leaves the script tag behind.
 *
 * - A **sold-out** variant (not available, no pre-order plan) shows the price
 *   row at its own price, hides Stay.ai, and swaps the dead Add button for
 *   Stoq's back-in-stock sign-up. A pre-order variant offers the same sign-up
 *   as a link under its button, for anyone who would rather wait than pay now
 *   -- once Stoq's "show on pre-order" setting is on; see stoqTakesPreorder.
 *
 * Each product-form-component is handled with its own map and its own variant
 * id, because the popup puts a second buy box on a product page.
 *
 * The sign-up is Stoq's own form (Restock Rocket), opened through
 * `window._RestockRocket.openModal(product, variantId)`, so sign-ups land in
 * Stoq and flow on to Klaviyo and Postscript from there exactly as its own
 * floating "Notify me" tab's did. That tab is hidden wherever this row is
 * ready (`assets/ora-custom.css`): one sign-up per page, in the buy box. Stoq
 * keeps the product it was loaded with in `_RestockRocketConfig.product`; on
 * a collection page there is none, and in a quick add the popup's product is
 * not the page's, so those fetch `<product url>.js`, which has the shape Stoq
 * reads (id, title, handle, options, featured_image, variants).
 */

/** @type {WeakMap<Element, {text: string, map: Record<string, any>}>} */
const cache = new WeakMap();

/**
 * @param {HTMLElement} pf
 * @returns {Record<string, any>}
 */
function mapFor(pf) {
  const text = pf.dataset.oraVariants || '';
  if (!text) return {};
  const hit = cache.get(pf);
  if (hit && hit.text === text) return hit.map;
  let map;
  try {
    map = JSON.parse(text);
  } catch {
    map = {};
  }
  cache.set(pf, { text, map });
  return map;
}

/** @param {HTMLElement} pf */
function currentId(pf) {
  const field = pf.querySelector('form input[name="id"]');
  return field instanceof HTMLInputElement ? String(field.value) : '';
}

/**
 * The Stay.ai widget that belongs to this buy box: the topmost box holding it
 * that does not also hold the buy buttons, so hiding it never hides those.
 * @param {HTMLElement} pf
 */
function stayWrapper(pf) {
  const scope = pf.closest('.product-details, dialog, .shopify-section') || document;
  const stay = scope.querySelector('[class*="stayai-"]');
  if (!stay) return null;
  const buttons = pf.closest('.buy-buttons-block');
  if (buttons) {
    let ancestor = buttons.parentElement;
    while (ancestor && !ancestor.contains(stay)) ancestor = ancestor.parentElement;
    if (ancestor) {
      let node = stay;
      while (node.parentElement && node.parentElement !== ancestor) node = node.parentElement;
      if (!node.contains(buttons)) return node;
    }
  }
  return stay.closest('[id^="shopify-block"]') || stay;
}

/**
 * @param {HTMLElement} pf
 * @param {boolean} hidden
 */
function setStayHidden(pf, hidden) {
  const wrapper = stayWrapper(pf);
  if (wrapper && wrapper.classList.contains('ora-stay-hidden') !== hidden) {
    wrapper.classList.toggle('ora-stay-hidden', hidden);
  }
}

/**
 * @param {Element | null} el
 * @param {string} text
 */
function setText(el, text) {
  if (el && el.textContent !== text) el.textContent = text;
}

/**
 * @param {Element | null} el
 * @param {boolean} hidden
 */
function setHidden(el, hidden) {
  if (el instanceof HTMLElement && el.hidden !== hidden) el.hidden = hidden;
}

/**
 * @param {HTMLElement} pf
 * @param {any} data
 */
function applyUI(pf, data) {
  const shipRow = pf.querySelector('[data-ora-shiprow]');
  if (data && data.note) {
    setText(pf.querySelector('[data-ora-shipdate]'), data.note);
    setHidden(shipRow, false);
  } else {
    setHidden(shipRow, true);
  }

  const priceRow = pf.querySelector('[data-ora-price-row]');
  if (!priceRow) return;
  if (!data) {
    setHidden(priceRow, true);
    return;
  }
  setText(pf.querySelector('[data-ora-price]'), data.priceNow || '');
  const compare = pf.querySelector('[data-ora-compare]');
  setText(compare, data.priceWas || '');
  setHidden(compare, !data.priceWas);
  const save = pf.querySelector('[data-ora-save]');
  if (data.save) {
    setText(save, `Save ${data.save}%`);
    setHidden(save, false);
  } else {
    setHidden(save, true);
  }
  setHidden(priceRow, false);
}

/**
 * @param {HTMLFormElement} form
 * @param {boolean} on
 */
function setOneOff(form, on) {
  if (form.hasAttribute('data-ora-oneoff') !== on) form.toggleAttribute('data-ora-oneoff', on);
  for (const field of form.querySelectorAll('input[name="selling_plan"], select[name="selling_plan"]')) {
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) continue;
    if (on) {
      if (!field.disabled) {
        field.disabled = true;
        field.setAttribute('data-ora-oneoff-off', '');
      }
    } else if (field.hasAttribute('data-ora-oneoff-off')) {
      field.disabled = false;
      field.removeAttribute('data-ora-oneoff-off');
    }
  }
}

/** @returns {any} */
function stoq() {
  const api = /** @type {any} */ (window)._RestockRocket;
  return api && typeof api.openModal === 'function' ? api : null;
}

/**
 * Whether Stoq's form will actually take this pre-order variant. It lists only
 * variants that are not available, plus -- with Stoq's "show on pre-order"
 * setting on -- continue-selling variants at or below zero, which is what a
 * pre-order is; with the setting off its form opens empty. That second test
 * reads the inventory of the product Stoq was loaded with, so a popup showing
 * some other product cannot pass it.
 * @param {HTMLElement} pf
 */
function stoqTakesPreorder(pf) {
  const config = /** @type {any} */ (window)._RestockRocketConfig;
  if (!config || !config.product || String(config.product.id) !== String(pf.dataset.productId)) return false;
  const settings = config.settings || config.cachedSettings || {};
  const id = Number(currentId(pf));
  if (settings.theme_config && settings.theme_config.allowInvalidVariants) return true;
  if (Array.isArray(config.enabledNotifyMeVariantIds) && config.enabledNotifyMeVariantIds.includes(id)) return true;
  return Boolean(settings.show_button_on_preorder);
}

/**
 * @param {HTMLElement} pf
 * @param {any} data
 */
function applyNotify(pf, data) {
  const row = pf.querySelector('[data-ora-notify]');
  if (!(row instanceof HTMLElement)) return;
  const mode = data && data.soldout ? 'soldout' : data && !data.single ? 'preorder' : '';
  if (row.getAttribute('data-mode') !== mode) row.setAttribute('data-mode', mode);
  const ready = Boolean(stoq()) && (mode !== 'preorder' || stoqTakesPreorder(pf));
  if (row.hasAttribute('data-ready') !== ready) row.toggleAttribute('data-ready', ready);
  setHidden(row, !(ready && mode));
}

/** @param {HTMLElement} pf */
function applyOne(pf) {
  const form = pf.querySelector('form');
  if (!(form instanceof HTMLFormElement)) return;
  const data = mapFor(pf)[currentId(pf)] || null;
  const preorder = Boolean(data && !data.single && !data.soldout);

  const flag = pf.querySelector('.ora-preorder-flag');
  if (flag && flag.getAttribute('data-preorder') !== String(preorder)) {
    flag.setAttribute('data-preorder', String(preorder));
  }

  let mine = form.querySelector('input[name="selling_plan"][data-ora-preorder]');
  if (preorder) {
    if (!(mine instanceof HTMLInputElement)) {
      mine = document.createElement('input');
      mine.setAttribute('type', 'hidden');
      mine.setAttribute('name', 'selling_plan');
      mine.setAttribute('data-ora-preorder', '');
      form.appendChild(mine);
    }
    if (mine instanceof HTMLInputElement && mine.value !== String(data.plan)) mine.value = data.plan;
    for (const other of form.querySelectorAll('input[name="selling_plan"]')) {
      if (other !== mine) other.remove();
    }
  } else {
    mine?.remove();
  }

  setOneOff(form, Boolean(data && data.single));
  applyUI(pf, data);
  applyNotify(pf, data);
  setStayHidden(pf, Boolean(data));
}

/** @type {Map<string, Promise<any>>} */
const productJson = new Map();

/**
 * The product object Stoq's form wants, or null to let it use its own.
 * @param {HTMLElement} pf
 */
async function stoqProduct(pf) {
  const config = /** @type {any} */ (window)._RestockRocketConfig;
  const id = pf.dataset.productId;
  if (config && config.product && String(config.product.id) === String(id)) return null;
  const url = pf.dataset.productUrl;
  if (!url) return null;
  const path = url.split('?')[0] + '.js';
  if (!productJson.has(path)) {
    productJson.set(
      path,
      fetch(path, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null)
    );
  }
  return productJson.get(path);
}

/**
 * Stoq's form lists only the variants Shopify calls unavailable. A variant
 * the `custom.sold_out` flag has taken off sale is still available to Shopify
 * (snippets/ora-sold-out.liquid), so Stoq would open an empty form for it.
 * Hand Stoq a copy of the product with every variant our map calls sold out
 * marked unavailable; the shopper signs up for that variant like any other.
 * @param {any} product - the product Stoq should use, or null for its own
 * @param {HTMLElement} pf
 */
function asSoldOut(product, pf) {
  const config = /** @type {any} */ (window)._RestockRocketConfig;
  const source = product || (config && config.product);
  if (!source || !Array.isArray(source.variants)) return product;
  const map = mapFor(pf);
  const copy = { ...source, variants: source.variants.map((v) => (map[String(v.id)] && map[String(v.id)].soldout ? { ...v, available: false } : v)) };
  return copy;
}

/** @param {MouseEvent} event */
async function openSignup(event) {
  const target = event.target;
  const trigger = target instanceof Element ? target.closest('[data-ora-notify-open]') : null;
  if (!trigger) return;
  const pf = trigger.closest('product-form-component');
  const api = stoq();
  if (!(pf instanceof HTMLElement) || !api) return;
  event.preventDefault();
  const variantId = Number(currentId(pf));
  const product = asSoldOut(await stoqProduct(pf), pf);

  // Stoq draws its form on the body, which a modal <dialog> in the top layer
  // would sit over, so the quick-add popup closes first.
  const dialog = trigger.closest('dialog');
  if (dialog instanceof HTMLDialogElement && dialog.open) {
    /** @type {any} */
    let host = dialog.parentElement;
    while (host && typeof host.closeDialog !== 'function') host = host.parentElement;
    if (host) await host.closeDialog();
    else dialog.close();
  }
  api.openModal(product, variantId);
}

function apply() {
  for (const pf of document.querySelectorAll('product-form-component')) {
    if (pf instanceof HTMLElement) applyOne(pf);
  }
}

let pending = false;
function schedule() {
  if (pending) return;
  pending = true;
  requestAnimationFrame(() => {
    pending = false;
    apply();
  });
}

function boot() {
  apply();
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('variant-picker')) {
      for (const delay of [0, 150, 350, 700, 1200]) setTimeout(apply, delay);
    }
  });
  window.addEventListener('load', apply);
  document.addEventListener('click', openSignup);
  // Stoq's script is deferred and can land seconds after ours, so the sign-up
  // row is offered once it has, and not before.
  if (!stoq()) {
    let tries = 0;
    const wait = setInterval(() => {
      tries += 1;
      if (stoq() || tries > 40) {
        clearInterval(wait);
        apply();
      }
    }, 250);
  }
}

if (document.readyState !== 'loading') boot();
else document.addEventListener('DOMContentLoaded', boot);
